import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { Image, Video, Film, Images, Clock3, ExternalLink, AlertCircle, CheckCircle2, Loader2, Wand2, MapPin, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { instagramService, PublishPostPayload, PublishResult, IgCarouselChild, IgAccountInfo } from '@/services/instagram.service';
import { MediaUpload } from '@/components/MediaUpload';
import { aiCampaignService } from '@/services/ai-campaign.service';
import { ModelPicker, ModelPickerValue } from '@/components/ModelPicker';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type MediaType = 'IMAGE' | 'VIDEO' | 'REELS' | 'CAROUSEL_ALBUM' | 'STORIES';

const TYPE_OPTIONS: Array<{ type: MediaType; icon: React.ReactNode; label: string; sub: string }> = [
  { type: 'IMAGE', icon: <Image size={20} />, label: 'Image', sub: 'Photo post' },
  { type: 'CAROUSEL_ALBUM', icon: <Images size={20} />, label: 'Carousel', sub: '2–10 items' },
  { type: 'VIDEO', icon: <Video size={20} />, label: 'Video', sub: 'Feed video' },
  { type: 'REELS', icon: <Film size={20} />, label: 'Reel', sub: 'Vertical short' },
  { type: 'STORIES', icon: <Clock3 size={20} />, label: 'Story', sub: '24-hour' },
];

export default function InstagramPublish() {
  const nav = useNavigate();
  const [mediaType, setMediaType] = useState<MediaType>('IMAGE');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  // Carousel children — Meta requires 2–10, each an image or a video.
  const [children, setChildren] = useState<IgCarouselChild[]>([{}, {}]);
  // Which carousel slot the uploader below the thumbnail strip is editing.
  const [activeChild, setActiveChild] = useState(0);
  // Which carousel slide the preview is showing.
  const [previewSlide, setPreviewSlide] = useState(0);
  const [account, setAccount] = useState<IgAccountInfo | null>(null);
  const [caption, setCaption] = useState('');
  const [shareToFeed, setShareToFeed] = useState(true);
  const [locationId, setLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyStartedAt, setBusyStartedAt] = useState<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [aiModel, setAiModel] = useState<ModelPickerValue | undefined>(undefined);
  const [enhancing, setEnhancing] = useState(false);

  // Tick a 1s clock while publishing so the UI can show "Processing — 0:42 elapsed".
  // Helps non-developers know the app isn't frozen during Meta's 90–120s video transcode.
  useEffect(() => {
    if (!busyStartedAt) { setElapsedSec(0); return; }
    const tick = () => setElapsedSec(Math.floor((Date.now() - busyStartedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [busyStartedAt]);

  // The preview is a mock of the real feed post, so it must show the account
  // that is actually being posted to — a placeholder handle made people ask
  // which account this publishes to.
  useEffect(() => {
    instagramService.account().then(setAccount).catch(() => undefined);
  }, []);

  const handle = account?.username ?? 'your_account';

  const enhance = async () => {
    setEnhancing(true);
    try {
      // The IG publish flow has only one copy field — the caption — so we
      // map it to `draftMessage` (Meta's "primary text" slot, the closest
      // semantic match) and use the resulting `message` field.
      const r = await aiCampaignService.enhanceAdCopy({
        draftMessage: caption || undefined,
        ai_provider: aiModel?.provider,
        ai_model: aiModel?.model,
      });
      if (r.message) setCaption(r.message);
      toast.success('Caption enhanced');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setEnhancing(false); }
  };

  const submit = async () => {
    setBusy(true);
    setBusyStartedAt(Date.now());
    try {
      const payload: PublishPostPayload = { media_type: mediaType, caption: caption || undefined };
      if (mediaType === 'CAROUSEL_ALBUM') {
        // Drop empty slots so a half-filled row can't fail validation server-side.
        payload.children = children.filter((c) => c.image_url || c.video_url);
      } else if (mediaType === 'IMAGE') {
        payload.image_url = imageUrl;
      } else if (mediaType === 'STORIES') {
        // A story is either a photo or a video — whichever the user filled in.
        if (videoUrl) payload.video_url = videoUrl;
        else payload.image_url = imageUrl;
      } else {
        payload.video_url = videoUrl;
        if (coverUrl) payload.cover_url = coverUrl;
      }
      if (mediaType === 'REELS') payload.share_to_feed = shareToFeed;
      if (locationId.trim()) payload.location_id = locationId.trim();

      const r = await instagramService.publish(payload);
      setResult(r);
      toast.success('Posted to Instagram!');
    } catch {
      /* toast already shown by axios interceptor */
    } finally {
      setBusy(false);
      setBusyStartedAt(null);
    }
  };

  const filledChildren = children.filter((c) => c.image_url || c.video_url);

  const removeChild = (i: number) => {
    setChildren((cs) => cs.filter((_, idx) => idx !== i));
    setActiveChild((a) => (a >= i && a > 0 ? a - 1 : a));
    setPreviewSlide(0);
  };

  /** Reorder a slide — carousel order is the order Instagram swipes through. */
  const moveChild = (from: number, to: number) => {
    if (to < 0 || to >= children.length) return;
    setChildren((cs) => {
      const next = [...cs];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setActiveChild(to);
    setPreviewSlide(to);
  };

  const ready = (() => {
    if (caption.length > 2200) return false;
    if (mediaType === 'CAROUSEL_ALBUM') return filledChildren.length >= 2;
    // A story accepts either a photo or a video.
    if (mediaType === 'STORIES') return !!imageUrl || !!videoUrl;
    if (mediaType === 'IMAGE') return !!imageUrl;
    return !!videoUrl;
  })();

  const isVideo = mediaType === 'VIDEO' || mediaType === 'REELS';
  const carouselSlides = mediaType === 'CAROUSEL_ALBUM' ? filledChildren : [];
  const slideIndex = Math.min(previewSlide, Math.max(0, carouselSlides.length - 1));
  const slide = carouselSlides[slideIndex];
  const storyMedia = mediaType === 'STORIES' ? (imageUrl || videoUrl) : '';
  const showPreview =
    (mediaType === 'IMAGE' && !!imageUrl) ||
    (isVideo && !!(coverUrl || videoUrl)) ||
    (mediaType === 'CAROUSEL_ALBUM' && carouselSlides.length > 0) ||
    (mediaType === 'STORIES' && !!storyMedia);
  const aspect = mediaType === 'REELS' || mediaType === 'STORIES' ? 'aspect-[9/16]' : 'aspect-square';

  /** Clear the composer so the next post starts from a blank slate. */
  const resetComposer = () => {
    setResult(null);
    setImageUrl('');
    setVideoUrl('');
    setCoverUrl('');
    setCaption('');
    setChildren([{}, {}]);
    setActiveChild(0);
    setPreviewSlide(0);
  };

  return (
    <>
      <Topbar
        title="New Instagram Post"
        breadcrumb="Instagram / Posts / New"
        right={
          <div className="flex items-center gap-2">
            {busy && elapsedSec > 0 && (
              <span className="text-2xs text-text-muted tabular-nums">
                {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')} elapsed
              </span>
            )}
            <Button variant="secondary" onClick={() => nav('/instagram/posts')} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={!ready || busy}
              onClick={submit}
              iconLeft={busy ? <Loader2 size={15} className="animate-spin" /> : undefined}
            >
              {busy
                ? (isVideo ? 'Processing video…' : 'Publishing…')
                : 'Publish post'}
            </Button>
          </div>
        }
      />

      {/* Single column until xl — below that the 7/5 split left the preview
          card narrower than the phone frame it is imitating. */}
      <div className="px-7 py-7 w-full grid grid-cols-1 gap-5 xl:grid-cols-12">
        {/* Left column — form */}
        <div className="space-y-4 xl:col-span-7">
          {/* Media type segmented control */}
          <Card>
            <SectionHeader title="Post type" />
            <div className="grid grid-cols-3 gap-3">
              {TYPE_OPTIONS.map(({ type, icon, label, sub }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMediaType(type)}
                  className={clsx(
                    'flex flex-col items-center justify-center gap-1.5 rounded-xl border p-4 text-sm font-semibold transition-all',
                    mediaType === type
                      ? 'border-primary bg-primary-soft text-primary shadow-soft'
                      : 'border-border bg-surface-alt text-text-muted hover:bg-surface hover:border-border-strong',
                  )}
                >
                  {icon}
                  <span>{label}</span>
                  <span className="text-[10px] font-normal opacity-70">{sub}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Media */}
          <Card>
            <SectionHeader title="Media" icon={<Image size={16} />} />

            {mediaType === 'CAROUSEL_ALBUM' && (
              <div className="space-y-3">
                <p className="text-xs text-text-muted">
                  2–10 items, shown in this order. Mix images and videos freely.
                </p>
                {/* Horizontal strip — slides read left to right in the same
                    order they swipe on Instagram. Click a tile to edit it. */}
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                  {children.map((child, i) => (
                    <div key={i} className="group relative h-24 w-24 shrink-0">
                      <button
                        type="button"
                        onClick={() => { setActiveChild(i); setPreviewSlide(i); }}
                        className={clsx(
                          'h-full w-full overflow-hidden rounded-xl border-2 transition-all',
                          activeChild === i
                            ? 'border-primary shadow-soft'
                            : 'border-border hover:border-border-strong',
                        )}
                      >
                        {child.image_url ? (
                          <img src={child.image_url} alt="" className="h-full w-full object-cover" />
                        ) : child.video_url ? (
                          <video src={child.video_url} muted className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full w-full flex-col items-center justify-center gap-1 bg-surface-alt text-text-muted">
                            <Plus size={16} />
                            <span className="text-[10px] font-medium">Empty</span>
                          </span>
                        )}
                      </button>
                      <span className="pointer-events-none absolute left-1 top-1 rounded-md bg-black/65 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        {i + 1}
                      </span>
                      {children.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeChild(i)}
                          title="Remove item"
                          className="absolute right-1 top-1 rounded-md bg-black/65 p-1 text-white opacity-0 transition-all hover:bg-danger focus:opacity-100 group-hover:opacity-100"
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  ))}
                  {children.length < 10 && (
                    <button
                      type="button"
                      onClick={() => { setChildren((cs) => [...cs, {}]); setActiveChild(children.length); }}
                      className="flex h-24 w-24 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-text-muted transition-colors hover:border-primary hover:text-primary"
                    >
                      <Plus size={18} />
                      <span className="text-[10px] font-semibold">Add item</span>
                    </button>
                  )}
                </div>

                {/* Uploader for the tile selected above. Remounting on slot
                    change resets MediaUpload's internal upload/URL tab. */}
                <div className="rounded-xl border border-border bg-surface-alt/40 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">
                      Item {activeChild + 1} of {children.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveChild(activeChild, activeChild - 1)}
                        disabled={activeChild === 0}
                        title="Move earlier"
                        className="rounded-lg p-1 text-text-muted transition-colors hover:bg-surface hover:text-text-primary disabled:opacity-30"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveChild(activeChild, activeChild + 1)}
                        disabled={activeChild === children.length - 1}
                        title="Move later"
                        className="rounded-lg p-1 text-text-muted transition-colors hover:bg-surface hover:text-text-primary disabled:opacity-30"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                  <MediaUpload
                    key={activeChild}
                    mode={children[activeChild]?.video_url ? 'video' : 'image'}
                    value={children[activeChild]?.image_url ?? children[activeChild]?.video_url ?? ''}
                    folder="instagram/carousel"
                    onChange={(url, meta) => {
                      const isVideo = (meta?.mime ?? '').startsWith('video/') || /\.(mp4|mov|m4v)$/i.test(url);
                      setChildren((cs) =>
                        cs.map((c, idx) => (idx === activeChild ? (isVideo ? { video_url: url } : { image_url: url }) : c)),
                      );
                      setPreviewSlide(activeChild);
                    }}
                  />
                </div>

                <p className="text-2xs text-text-muted">
                  {filledChildren.length} of {children.length} slots filled
                  {filledChildren.length < 2 && ' — Instagram needs at least 2'}
                </p>
              </div>
            )}

            {(mediaType === 'IMAGE' || mediaType === 'STORIES') && (
              <FormField
                label={mediaType === 'STORIES' ? 'Story photo' : 'Image'}
                required={mediaType === 'IMAGE'}
                hint={mediaType === 'STORIES'
                  ? 'JPEG/PNG · vertical 9:16 (1080×1920 ideal) · leave blank if using a video'
                  : 'JPEG only · ≤8 MB · 320–1440 px wide · aspect 4:5 to 1.91:1'}
              >
                <MediaUpload mode="image" value={imageUrl} folder="instagram" onChange={(url) => setImageUrl(url)} />
              </FormField>
            )}

            {(isVideo || mediaType === 'STORIES') && (
              <>
                <FormField
                  label={mediaType === 'STORIES' ? 'Story video' : 'Video'}
                  required={isVideo}
                  hint={
                    mediaType === 'STORIES'
                      ? 'MP4/MOV · ≤15 s · vertical 9:16'
                      : mediaType === 'REELS'
                        ? 'MP4/MOV · ≤100 MB · ≤90 s · vertical 9:16 (1080×1920 ideal)'
                        : 'MP4/MOV · ≤100 MB · 3 s–60 min'
                  }
                >
                  <MediaUpload mode="video" value={videoUrl} folder="instagram" onChange={(url) => setVideoUrl(url)} />
                </FormField>
                {isVideo && (
                  <FormField label="Cover image" hint="Optional — used as the video thumbnail">
                    <MediaUpload mode="image" value={coverUrl} folder="instagram/covers" onChange={(url) => setCoverUrl(url)} />
                  </FormField>
                )}
              </>
            )}

            {mediaType === 'REELS' && (
              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface-alt px-4 py-3 transition-colors hover:bg-surface">
                <input
                  type="checkbox"
                  checked={shareToFeed}
                  onChange={(e) => setShareToFeed(e.target.checked)}
                  className="h-4 w-4 rounded accent-primary"
                />
                <span className="text-sm font-medium text-text-primary">Also share to feed</span>
                <span className="ml-auto text-xs text-text-muted">Recommended</span>
              </label>
            )}
          </Card>

          {/* Caption */}
          <Card>
            <SectionHeader title="Caption" subtitle={`${caption.length} / 2200 chars`} />

            {/* AI enhance bar */}
            <div className="mb-3 rounded-xl border border-primary/30 bg-primary-soft/40 p-2.5">
              <div className="flex items-start gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-white">
                  <Wand2 size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-text-primary">Enhance with AI</div>
                  <div className="text-2xs text-text-muted">
                    Type a rough caption below, then click Enhance to polish it for Instagram.
                  </div>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-end justify-end gap-2">
                <ModelPicker value={aiModel} onChange={setAiModel} size="sm" label="AI model" />
                <Button onClick={enhance} loading={enhancing}>
                  <Wand2 size={12} /> Enhance
                </Button>
              </div>
            </div>

            <FormField label="Caption text">
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={6}
                maxLength={2200}
                placeholder="Your caption — emojis, line breaks, and #hashtags all work."
                className={caption.length > 2200 ? 'border-danger focus:border-danger' : undefined}
              />
            </FormField>

            {/* Optional location tag — Meta accepts a Page ID that represents
                a location. We don't auto-suggest because Meta restricts the
                /pages/search?type=place endpoint. Users can paste an ID
                copied from Instagram or Facebook. */}
            <FormField
              label="Location tag (optional)"
              hint="Paste the Facebook Page ID for a place. Find one by searching the place on facebook.com → About → Page ID."
            >
              <Input
                value={locationId}
                onChange={(e) => setLocationId(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g. 102158453165146"
                iconLeft={<MapPin size={14} />}
                inputMode="numeric"
              />
            </FormField>
          </Card>

          {/* Video timing note — extra urgency when actively publishing. */}
          {isVideo && (
            <div className={clsx(
              'flex items-start gap-3 rounded-xl border px-4 py-3',
              busy
                ? 'border-warning/40 bg-warning-soft'
                : 'border-info/30 bg-info-soft',
            )}>
              {busy
                ? <Loader2 size={15} className="mt-0.5 shrink-0 text-warning animate-spin" />
                : <AlertCircle size={15} className="mt-0.5 shrink-0 text-info" />}
              <div className={clsx('text-xs leading-relaxed', busy ? 'text-warning' : 'text-info')}>
                {busy ? (
                  <>
                    <strong>Processing video on Meta…</strong> This usually takes 30–90 seconds. We poll until it's ready, then publish to your feed.
                    {elapsedSec > 0 && (
                      <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 font-mono tabular-nums">
                        {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')}
                      </span>
                    )}
                    <span className="block mt-0.5 text-text-muted">Keep this tab open. We'll show the success screen automatically.</span>
                  </>
                ) : (
                  'Video uploads can take up to 2 minutes while Meta processes the file. Don\'t close the page until you see the success screen.'
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column — preview */}
        <div className="xl:col-span-5">
          <div className="sticky top-4">
            <div className="text-2xs mb-2 font-semibold uppercase tracking-[0.14em] text-text-muted">
              Preview
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-white text-black shadow-soft max-w-xs">
              {/* Instagram-style header */}
              <div className="flex items-center gap-2 border-b border-black/10 px-3 py-2">
                {account?.profile_picture_url ? (
                  <img
                    src={account.profile_picture_url}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                    onError={(e) => ((e.target as HTMLImageElement).style.visibility = 'hidden')}
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888]" />
                )}
                <span className="truncate text-xs font-semibold">{handle}</span>
                {mediaType === 'REELS' && (
                  <span className="ml-auto rounded bg-black/10 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                    Reel
                  </span>
                )}
                {mediaType === 'STORIES' && (
                  <span className="ml-auto rounded bg-black/10 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                    Story
                  </span>
                )}
              </div>

              {/* Media area */}
              <div className={`${aspect} bg-surface-alt relative overflow-hidden`}>
                {mediaType === 'IMAGE' && imageUrl && (
                  <img
                    src={imageUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                  />
                )}
                {mediaType === 'STORIES' && storyMedia && (
                  imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                    />
                  ) : (
                    <video
                      src={videoUrl}
                      className="h-full w-full object-cover"
                      muted
                      onError={(e) => ((e.target as HTMLVideoElement).style.display = 'none')}
                    />
                  )
                )}
                {isVideo && coverUrl && (
                  <img
                    src={coverUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                  />
                )}
                {isVideo && !coverUrl && videoUrl && (
                  <video
                    src={videoUrl}
                    className="h-full w-full object-cover"
                    muted
                    onError={(e) => ((e.target as HTMLVideoElement).style.display = 'none')}
                  />
                )}

                {/* Carousel — swipe through the filled slides, same order as the strip */}
                {mediaType === 'CAROUSEL_ALBUM' && slide && (
                  <>
                    {slide.image_url ? (
                      <img src={slide.image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <video src={slide.video_url} className="h-full w-full object-cover" muted />
                    )}
                    {carouselSlides.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setPreviewSlide((s) => Math.max(0, s - 1))}
                          disabled={slideIndex === 0}
                          className="absolute left-1.5 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-1 text-white transition-opacity hover:bg-black/65 disabled:opacity-0"
                          aria-label="Previous slide"
                        >
                          <ChevronLeft size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewSlide((s) => Math.min(carouselSlides.length - 1, s + 1))}
                          disabled={slideIndex === carouselSlides.length - 1}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-1 text-white transition-opacity hover:bg-black/65 disabled:opacity-0"
                          aria-label="Next slide"
                        >
                          <ChevronRight size={14} />
                        </button>
                        <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1">
                          {carouselSlides.map((_, i) => (
                            <span
                              key={i}
                              className={clsx(
                                'h-1.5 w-1.5 rounded-full transition-colors',
                                i === slideIndex ? 'bg-white' : 'bg-white/45',
                              )}
                            />
                          ))}
                        </div>
                        <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">
                          {slideIndex + 1}/{carouselSlides.length}
                        </span>
                      </>
                    )}
                  </>
                )}

                {!showPreview && (
                  <div className="flex h-full w-full items-center justify-center px-4 text-center text-xs text-text-muted">
                    {mediaType === 'CAROUSEL_ALBUM'
                      ? 'Fill at least 2 carousel items to preview'
                      : 'Add media to preview'}
                  </div>
                )}
              </div>

              {/* Caption preview */}
              <div className="px-3 py-2 text-xs leading-snug whitespace-pre-wrap">
                {caption ? (
                  <>
                    <span className="font-semibold">{handle}</span> {caption}
                  </>
                ) : (
                  <span className="text-text-muted">Your caption will appear here</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success confirmation — a dialog over the composer rather than a
          separate screen, so the post that was just made stays on screen. */}
      <Modal
        open={!!result}
        onClose={resetComposer}
        title="Posted to Instagram"
        widthClass="max-w-md"
      >
        <div className="flex flex-col items-center gap-4 py-2 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-success-soft text-success">
            <CheckCircle2 size={28} />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-text-primary">Live on Instagram!</h3>
            <p className="mt-1 text-sm text-text-muted">
              Media ID: <span className="font-mono text-xs">{result?.mediaId}</span>
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 pt-1">
            {result?.permalink && (
              <a href={result.permalink} target="_blank" rel="noreferrer" className="w-full">
                <Button className="w-full" iconRight={<ExternalLink size={14} />}>View on Instagram</Button>
              </a>
            )}
            <Button variant="secondary" onClick={() => nav('/instagram/posts')}>
              See all posts
            </Button>
            <Button variant="ghost" onClick={resetComposer}>
              Post another
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
